/* SubjectFooter — sticky bottom footer that reveals subject-specific interactive
 * tools/props based on the currently selected subject. Detected by the subject
 * name substring match (e.g. any subject containing "physics" gets the physics kit).
 *
 * Clicking a tool adds it to the whiteboard scene as a draggable, resizable
 * element via the parent's `onAddProp` callback.
 */
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  FlaskConical, Magnet, Zap, Atom, Ruler, Compass, Sigma, ChevronUp,
  ChevronDown, Lightbulb, TestTube, Pipette, Beaker, Cog, Wind, Waves,
  Dna, Leaf, HeartPulse, Globe2, MessageSquare, Quote, Search, MinusCircle,
  PlusCircle, Send, X, Loader2, Sparkles,
} from "lucide-react";
import { SUBJECT_KITS } from "./subjectKits";
import api from "@/lib/api";

const ICONS = {
  FlaskConical, Magnet, Zap, Atom, Ruler, Compass, Sigma, Lightbulb, TestTube,
  Pipette, Beaker, Cog, Wind, Waves, Dna, Leaf, HeartPulse, Globe2,
  MessageSquare, Quote,
};

export default function SubjectFooter({ subject, onAddProp, onRequestTool }) {
  const [collapsed, setCollapsed] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);

  const kit = useMemo(() => detectKit(subject), [subject]);

  if (!subject) return null;

  return (
    <motion.div
      initial={{ y: 60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 60, opacity: 0 }}
      transition={{ type: "spring", stiffness: 240, damping: 26 }}
      className="absolute left-1/2 -translate-x-1/2 bottom-3 z-30 bg-white border border-black/10 rounded-xl shadow-2xl overflow-hidden max-w-[calc(100%-2rem)]"
      data-testid="subject-footer"
    >
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#FFFBEA] to-white border-b border-black/5">
        <Sparkles size={12} className="text-[#B45309]" />
        <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-700 font-bold">
          {kit.label} kit · {subject.name}
        </span>
        <button onClick={() => setReqOpen(true)} data-testid="subject-footer-request"
          className="ml-2 text-[10px] font-mono uppercase tracking-wider text-[#6366F1] hover:underline inline-flex items-center gap-1">
          <Send size={10} /> Request a tool
        </button>
        <div className="flex-1" />
        <button onClick={() => setCollapsed((v) => !v)} data-testid="subject-footer-collapse"
          className="w-6 h-6 rounded flex items-center justify-center hover:bg-black/5" title={collapsed ? "Expand" : "Collapse"}>
          {collapsed ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="flex items-stretch gap-1 p-2 overflow-x-auto" data-testid="subject-footer-scroll">
              {kit.tools.map((t) => {
                const Icon = ICONS[t.icon] || Sparkles;
                return (
                  <button key={t.id} onClick={() => { onAddProp(t); toast.success(`Added · ${t.label}`); }}
                    data-testid={`subject-tool-${t.id}`}
                    className="min-w-[92px] group flex flex-col items-center gap-1 px-2 py-2 rounded-md border border-transparent hover:border-[#0A0C10] hover:bg-neutral-50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-md flex items-center justify-center text-white shadow-sm" style={{ backgroundColor: t.color || kit.color }}>
                      <Icon size={18} />
                    </div>
                    <span className="text-[10px] font-medium text-[#0A0C10] text-center leading-tight">{t.label}</span>
                  </button>
                );
              })}
              {kit.tools.length === 0 && (
                <div className="px-4 py-3 text-xs text-neutral-500">
                  No preset tools for this subject yet. Click <span className="font-semibold">Request a tool</span> above to send us your idea.
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {reqOpen && <ToolRequestModal subject={subject} onClose={() => setReqOpen(false)} />}
    </motion.div>
  );
}

function detectKit(subject) {
  const name = ((subject?.name) || "").toLowerCase();
  const match = Object.entries(SUBJECT_KITS).find(([k]) => name.includes(k));
  if (match) return match[1];
  // Fallback: show a general kit
  return SUBJECT_KITS.general;
}

// ─────────── Tool-request modal ───────────
function ToolRequestModal({ subject, onClose }) {
  const [toolName, setToolName] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState("tool");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!toolName.trim() || !description.trim()) {
      toast.error("Give it a name and short description");
      return;
    }
    setBusy(true);
    try {
      await api.post("/tool-requests", {
        subject: subject?.name || "",
        tool_name: toolName.trim(),
        kind,
        description: description.trim(),
      });
      toast.success("Request sent · admin notified");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="tool-request-modal">
      <div className="bg-white border border-black/10 rounded-xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between bg-gradient-to-r from-[#F5F3FF] to-white">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500">Request a tool</div>
            <div className="text-sm font-bold text-[#0A0C10]">For {subject?.name || "your subject"}</div>
          </div>
          <button onClick={onClose} data-testid="tool-request-close" className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-black/5"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-1">Kind</div>
            <div className="flex gap-1">
              {["tool", "simulation", "template"].map((k) => (
                <button key={k} onClick={() => setKind(k)} data-testid={`tool-request-kind-${k}`}
                  className={`flex-1 text-[10px] uppercase font-mono px-2 py-1.5 rounded-md tracking-wider ${kind === k ? "bg-[#0A0C10] text-white" : "border border-neutral-300"}`}>{k}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-1">Tool name</div>
            <input autoFocus value={toolName} onChange={(e) => setToolName(e.target.value)}
              placeholder="e.g. Circuit simulator with resistors"
              data-testid="tool-request-name"
              className="w-full text-sm border border-neutral-200 rounded-md p-2 outline-none focus:border-[#0A0C10]" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-1">Describe what it should do</div>
            <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="How would you use it in a class? What should students see?"
              data-testid="tool-request-description"
              className="w-full text-sm border border-neutral-200 rounded-md p-2 outline-none focus:border-[#0A0C10]" />
          </div>
          <button onClick={submit} disabled={busy} data-testid="tool-request-submit"
            className="w-full bg-[#6366F1] text-white font-semibold py-2 text-sm rounded-md disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send request
          </button>
          <div className="text-[10px] text-neutral-500 text-center">Admin gets notified by email and can track this in their dashboard.</div>
        </div>
      </div>
    </div>
  );
}
